import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Modal } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { addTeamsToStandings, AddTeamsToStandingsRequest, removeTeamFromStandings, RemoveTeamFromStandingsRequest } from '../api/tournament/standingsService';
import { listTeams } from '../api/tournament/teamService';
import { getAllAgeCategoriesPaginated } from '../api/tournament/ageCategoryService';
import secureApi from '../utils/secureApi';
import ShimmerLoader from './ShimmerLoader';
import { getImageUrl } from '../utils/imageUrl';
import { getSportValue as getSportValueUtil } from '../utils/getSportValue';

interface TeamStanding {
  team: {
    _id: string;
    name: string;
    shortName: string;
    logo?: string | null;
  };
  played: number;
  won: number;
  lost: number;
  drawn: number;
  goalsFor: number;
  goalsAgainst: number;
  points: number;
  isQualified?: boolean;
}

interface TeamManagementCardProps {
  // Required props
  tournamentId: string;
  ageCategoryId: string;
  ageCategoryName?: string; // Age category name (e.g., "U7") for API filtering
  standings: TeamStanding[];
  onStandingsUpdate: () => Promise<void>;
  
  // For group management - to filter out teams already in other groups
  allGroupStandings?: TeamStanding[][]; // Array of standings arrays for all groups
  
  // Optional props for different contexts
  groupName?: string; // For group standings
  contextTitle?: string; // e.g., "Group A Teams" or "Standings Teams"
  
  // Season filtering
  tournamentSeasonId?: string; // Tournament season ID to filter teams by season
  
  // State management
  showCard: boolean;
  onCloseCard: () => void;
  isEditMode: boolean;
  onSetEditMode: (mode: boolean) => void;
  
  // Loading states
  addingTeams: boolean;
  onSetAddingTeams: (loading: boolean) => void;
  
  // Group name input control
  showGroupNameInput?: boolean; // New prop to control group name input visibility
  onGroupNameChange?: (name: string) => void; // New prop for group name change callback
}

const TeamManagementCard: React.FC<TeamManagementCardProps> = ({
  tournamentId,
  ageCategoryId,
  ageCategoryName,
  standings,
  onStandingsUpdate,
  allGroupStandings,
  groupName,
  contextTitle = "Teams",
  tournamentSeasonId,
  showCard,
  onCloseCard,
  isEditMode,
  onSetEditMode,
  addingTeams,
  onSetAddingTeams,
  showGroupNameInput = false,
  onGroupNameChange,
}) => {
  const location = useLocation();
  const [availableTeams, setAvailableTeams] = useState<any[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<any[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [editableGroupName, setEditableGroupName] = useState(groupName || '');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<TeamStanding | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadAvailableTeams = async (editMode?: boolean) => {
    if (!tournamentId || !ageCategoryId) return;

    try {
      setTeamsLoading(true);
      
      console.log('=== Loading Teams Debug ===');
      console.log('Tournament ID:', tournamentId);
      console.log('Age Category ID:', ageCategoryId);
      console.log('Age Category Name:', ageCategoryName);
      console.log('Tournament Season ID:', tournamentSeasonId);
      console.log('Group Name:', groupName);
      console.log('Context:', groupName ? 'Group Standings' : 'Single Standings');
      console.log('Edit Mode:', editMode);
      console.log('Current Standings Length:', standings.length);
      
      // Try multiple API approaches
      let teamsFound = false;
      
      // Approach 1: Use secureApi to call teams API with tournament and age category
      try {
        console.log('Making API call to get-all-teams with secureApi');
        console.log('API Parameters:', {
          page: 1,
          pageSize: 1000,
          tournament: tournamentId,
          ageCategory: ageCategoryName || ageCategoryId
        });
        
        // Use secureApi instead of direct axios - this properly handles token management
        const directResponse = await secureApi.get<any>(
          '/api/v2/tournaments/admin/teams/get-all-teams',
          {
            params: { 
              page: 1, 
              pageSize: 1000, 
              tournament: tournamentId, 
              ageCategory: ageCategoryName || ageCategoryId, // Use age category name if available, fallback to ID
              sport: getSportValueUtil(null, location),
              ...(tournamentSeasonId && { tournamentSeason: tournamentSeasonId }) // Pass tournamentSeason if available
            }
          }
        );
        
        console.log('SecureApi Response (with filters):', directResponse);
        
        // Handle different response structures
        let teamsData = null;
        if (directResponse && directResponse.data && directResponse.data.paginatedResults) {
          teamsData = directResponse.data;
        } else if (directResponse && directResponse.paginatedResults) {
          teamsData = directResponse;
        } else if (directResponse && directResponse.data) {
          teamsData = directResponse.data;
        } else {
          teamsData = directResponse;
        }
        
        if (teamsData && teamsData.paginatedResults && teamsData.paginatedResults.length > 0) {
          const teamsToUse = teamsData.paginatedResults;
          // NEW API STRUCTURE: _id is teamSeason id, team._id is the actual team id
          // Transform teams to use the nested team object
          const validTeams = (Array.isArray(teamsToUse) ? teamsToUse : []).map((teamItem: any) => {
            const teamData = teamItem.team || teamItem; // Use nested team object if available
            return {
              _id: teamData._id || teamItem._id, // Use team._id (actual team ID) first, fallback to _id
              name: teamData.name || teamItem.name || '',
              shortName: teamData.shortName || teamItem.shortName || '',
              logo: teamData.logo || teamItem.logo || null,
              // Keep original structure for reference
              originalTeam: teamItem
            };
          }).filter(team => 
            team && team._id && (team.name || team.shortName)
          );
          console.log('Teams loaded from secureApi (with filters):', teamsToUse.length);
          console.log('Valid teams after filtering:', validTeams.length);
          console.log('First few teams:', validTeams.slice(0, 3));
          setAvailableTeams(validTeams);
          teamsFound = true;
        } else {
          console.log('No teams found in API response with age category filter');
          console.log('Response structure:', {
            hasData: !!directResponse,
            hasPaginatedResults: !!(teamsData && teamsData.paginatedResults),
            paginatedResultsLength: teamsData?.paginatedResults?.length || 0,
            totalCount: teamsData?.totalCount || 0
          });
        }
      } catch (directErr: any) {
        console.error('SecureApi call with filters failed:', directErr);
        console.error('Error status:', directErr.response?.status);
        console.error('Error data:', directErr.response?.data);
        console.error('Error message:', directErr.message);
      }
      
      // If no teams found with age category filter, try without age category filter
      if (!teamsFound) {
        console.log('No teams found for age category:', ageCategoryId);
        console.log('Trying to load teams without age category filter...');
        
        try {
          // Use secureApi instead of direct axios
          const fallbackResponse = await secureApi.get<any>(
            '/api/v2/tournaments/admin/teams/get-all-teams',
            {
              params: { 
                page: 1, 
                pageSize: 1000, 
                tournament: tournamentId,
                sport: getSportValueUtil(null, location),
                // No age category filter
                ...(tournamentSeasonId && { tournamentSeason: tournamentSeasonId }) // Pass tournamentSeason if available
              }
            }
          );
          
          console.log('Fallback API Response (no age category filter):', fallbackResponse);
          
          // Handle different response structures from secureApi
          let fallbackTeamsData = null;
          if (fallbackResponse && fallbackResponse.data && fallbackResponse.data.paginatedResults) {
            fallbackTeamsData = fallbackResponse.data;
          } else if (fallbackResponse && fallbackResponse.paginatedResults) {
            fallbackTeamsData = fallbackResponse;
          } else if (fallbackResponse && fallbackResponse.data) {
            fallbackTeamsData = fallbackResponse.data;
          } else {
            fallbackTeamsData = fallbackResponse;
          }
          
          if (fallbackTeamsData && fallbackTeamsData.paginatedResults && fallbackTeamsData.paginatedResults.length > 0) {
            const allTeams = fallbackTeamsData.paginatedResults;
            console.log('Found', allTeams.length, 'teams for tournament (all age categories)');
            
            // Filter teams by age category on client side
            // NEW API STRUCTURE: _id is teamSeason id, team._id is the actual team id
            // First transform teams to use the nested team object
            const validAllTeams = (Array.isArray(allTeams) ? allTeams : []).map((teamItem: any) => {
              const teamData = teamItem.team || teamItem; // Use nested team object if available
              return {
                _id: teamData._id || teamItem._id, // Use team._id (actual team ID) first, fallback to _id
                name: teamData.name || teamItem.name || '',
                shortName: teamData.shortName || teamItem.shortName || '',
                logo: teamData.logo || teamItem.logo || null,
                ageCategory: teamItem.ageCategory || teamData.ageCategory, // Keep ageCategory from original
                ageCategoryId: teamItem.ageCategoryId || teamData.ageCategoryId, // Keep ageCategoryId from original
                // Keep original structure for reference
                originalTeam: teamItem
              };
            }).filter((team: any) => 
              team && team._id
            );
            
            const filteredTeams = validAllTeams.filter((team: any) => {
              // Handle both string and array formats for ageCategory
              let teamAgeCategories = [];
              
              if (Array.isArray(team.ageCategory)) {
                // If ageCategory is an array, extract the age category names/IDs
                teamAgeCategories = team.ageCategory.map((ac: any) => {
                  if (typeof ac === 'string') return ac;
                  if (typeof ac === 'object' && ac !== null) {
                    return ac.ageCategory || ac._id || ac.id || ac;
                  }
                  return ac;
                });
              } else if (team.ageCategory) {
                // If ageCategory is a string or object
                teamAgeCategories = [team.ageCategory];
              }
              
              // Also check other possible fields
              if (team.ageCategoryId) {
                teamAgeCategories.push(team.ageCategoryId);
              }
              
              console.log('Team age category check:', {
                teamName: team?.name || 'Unknown',
                teamAgeCategory: team?.ageCategory,
                teamAgeCategories: teamAgeCategories,
                targetAgeCategoryId: ageCategoryId,
                targetAgeCategoryName: ageCategoryName,
                matches: teamAgeCategories.includes(ageCategoryId) || 
                         teamAgeCategories.includes(ageCategoryName) ||
                         teamAgeCategories.some((ac: any) => ac === ageCategoryId) ||
                         teamAgeCategories.some((ac: any) => ac === ageCategoryName)
              });
              
              // Check if any of the team's age categories match our target
              return teamAgeCategories.includes(ageCategoryId) || 
                     teamAgeCategories.includes(ageCategoryName) ||
                     teamAgeCategories.some((ac: any) => ac === ageCategoryId) ||
                     teamAgeCategories.some((ac: any) => ac === ageCategoryName);
            });
            
            console.log('Teams after client-side age category filtering:', filteredTeams.length);
            console.log('Sample team age category data:', validAllTeams.slice(0, 3).map((t: any) => ({
              name: t?.name || 'Unknown',
              ageCategory: t?.ageCategory,
              ageCategoryId: t?.ageCategoryId,
              ageCategoryArray: Array.isArray(t?.ageCategory) ? t.ageCategory : 'Not an array',
              firstAgeCategoryItem: Array.isArray(t?.ageCategory) && t.ageCategory.length > 0 ? t.ageCategory[0] : 'N/A'
            })));
            
            // Teams are already transformed, just filter for required properties
            const validFilteredTeams = filteredTeams.filter((team: any) => 
              team && team._id && (team.name || team.shortName)
            );
            
            if (validFilteredTeams.length > 0) {
              setAvailableTeams(validFilteredTeams);
              teamsFound = true;
            } else {
              console.log('No teams found for age category after client-side filtering');
              console.log('This means the age category ID does not match any team data');
              console.log('Available age category IDs in teams:', Array.from(new Set(validAllTeams.flatMap((t: any) => {
                if (!t) return [];
                let categories = [];
                if (Array.isArray(t.ageCategory)) {
                  categories = t.ageCategory.map((ac: any) => {
                    if (typeof ac === 'string') return ac;
                    if (typeof ac === 'object' && ac !== null) {
                      return ac.ageCategory || ac._id || ac.id || ac;
                    }
                    return ac;
                  });
                } else if (t.ageCategory) {
                  categories = [t.ageCategory];
                }
                if (t.ageCategoryId) categories.push(t.ageCategoryId);
                return categories;
              }).filter(Boolean))));
              
              // For group management, don't show all teams as fallback
              // Instead, show empty array and let the user know no teams are available
              setAvailableTeams([]);
            }
          } else {
            console.log('No teams found for tournament either');
            setAvailableTeams([]);
          }
        } catch (fallbackErr: any) {
          console.error('Fallback API call failed:', fallbackErr);
          setAvailableTeams([]);
        }
        
        if (!teamsFound) {
          console.log('No teams available for this age category');
          setAvailableTeams([]);
          return;
        }
      }
      
      if (teamsFound) {
        return;
      }
      
      // Fallback to original listTeams function
      // Get sport for API call
      let sport: string;
      try {
        sport = getSportValueUtil(null, location);
      } catch (sportError) {
        // Fallback to Football if sport cannot be determined
        sport = 'Football';
      }
      
      let response = await listTeams({
        tournamentId: tournamentId,
        ageCategory: ageCategoryName || ageCategoryId, // Use age category name if available, fallback to ID
        page: 1,
        pageSize: 1000,
        tournamentSeason: tournamentSeasonId || undefined, // Pass tournamentSeason if available
        sport: sport // Pass sport parameter
      });
      
      console.log('API Response with age category:', response);
      
      if (response && (response.teams || response.paginatedResults)) {
        const teamsToUse = response.teams || response.paginatedResults;
        
        // NEW API STRUCTURE: _id is teamSeason id, team._id is the actual team id
        // Transform teams to use the nested team object
        const validTeams = (Array.isArray(teamsToUse) ? teamsToUse : []).map((teamItem: any) => {
          const teamData = teamItem.team || teamItem; // Use nested team object if available
          return {
            _id: teamData._id || teamItem._id, // Use team._id (actual team ID) first, fallback to _id
            name: teamData.name || teamItem.name || '',
            shortName: teamData.shortName || teamItem.shortName || '',
            logo: teamData.logo || teamItem.logo || null,
            // Keep original structure for reference
            originalTeam: teamItem
          };
        }).filter(team => 
          team && team._id && (team.name || team.shortName)
        );
        
        console.log('Teams loaded from API:', teamsToUse.length);
        console.log('Valid teams after filtering:', validTeams.length);
        console.log('Group name:', groupName);
        console.log('Current standings:', standings.length);
        
        // For both single and group standings, filter by age category
        // Teams can be in multiple groups or moved between groups
        if (groupName) {
          // For group standings, show all teams for the age category
          // The dropdown will filter out teams already in this specific group
          console.log('Setting teams for group standings (age category filtered):', validTeams.length);
          setAvailableTeams(validTeams);
        } else {
          // For single standings, show all teams for the age category
          // The dropdown will filter out teams already in single standings
          console.log('Setting teams for single standings (age category filtered):', validTeams.length);
          setAvailableTeams(validTeams);
        }
      } else {
        console.log('No teams found in API response');
        setAvailableTeams([]);
      }
    } catch (err: any) {
      console.error('Error loading teams:', err);
      console.error('Error details:', err.response?.data);
      console.error('Error status:', err.response?.status);
      setAvailableTeams([]);
    } finally {
      setTeamsLoading(false);
    }
  };

  const handleTeamSelection = (selectedOptions: any) => {
    setSelectedTeams(selectedOptions || []);
  };

  const handleAddTeams = async () => {
    if (selectedTeams.length === 0) return;
    
    try {
      onSetAddingTeams(true);
      
      // Get only newly selected teams (not existing ones)
      const selectedTeamData = selectedTeams.map(team => ({
        id: team.value,
        name: team.label
      }));

      const requestData: AddTeamsToStandingsRequest = {
        tournamentCategoryId: ageCategoryId,
        ...(editableGroupName && { groupName: editableGroupName }),
        teams: selectedTeamData
      };

      await addTeamsToStandings(requestData, getSportValueUtil(null, location));
      
      // Refresh standings data
      await onStandingsUpdate();
      
      // Show success message
      toast.success(`${isEditMode ? 'Teams updated' : 'Teams added'} successfully!`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Close card and reset state
      onCloseCard();
      setSelectedTeams([]);
      onSetEditMode(false);
      
    } catch (err: any) {
      console.error('Error adding teams:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update teams';
      toast.error(`Error: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      onSetAddingTeams(false);
    }
  };

  const handleRemoveTeam = (team: TeamStanding) => {
    setTeamToDelete(team);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!teamToDelete || !teamToDelete.team || !teamToDelete.team._id) {
      toast.error('Invalid team data', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    try {
      setDeleting(true);
      
      const contextName = groupName ? `Group ${editableGroupName}` : 'standings';
      
      const requestData: RemoveTeamFromStandingsRequest = {
        tournamentId: tournamentId,
        ageCategoryId: ageCategoryId,
        teamId: teamToDelete.team._id,
        ...(editableGroupName && { groupName: editableGroupName })
      };

      await removeTeamFromStandings(requestData, getSportValueUtil(null, location));
      await onStandingsUpdate();
      
      toast.success(`${teamToDelete.team.name || 'Team'} has been removed from ${contextName}.`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      setShowDeleteModal(false);
      setTeamToDelete(null);
    } catch (err: any) {
      console.error('Error removing team:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to remove team';
      toast.error(`Error removing team: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  // Load teams when card opens or when season changes
  useEffect(() => {
    console.log('TeamManagementCard useEffect triggered:', {
      showCard,
      isEditMode,
      groupName,
      tournamentId,
      ageCategoryId,
      tournamentSeasonId,
      standingsLength: standings.length
    });
    
    if (showCard) {
      loadAvailableTeams(isEditMode);
    }
  }, [showCard, isEditMode, tournamentSeasonId, tournamentId, ageCategoryId]);

  if (!showCard) return null;

  return (
    <div className="mb-3">
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom-0 py-3">
          <h5 className="mb-0 fw-semibold">
            <i className={isEditMode ? "ri-edit-circle-line me-2 text-primary" : "ri-add-circle-line me-2 text-primary"}></i>
            {isEditMode ? 'Edit Teams' : 'Add Teams'}
          </h5>
        </Card.Header>
        <Card.Body className="p-3">
          <div>
            {/* Group Name Field - Only show when explicitly requested */}
            {showGroupNameInput && (
              <div className="mb-3">
                <h6 className="fw-semibold text-primary mb-2">
                  <i className="ri-group-line me-2"></i>
                  Group Name
                </h6>
                <Form.Control
                  type="text"
                  value={editableGroupName}
                  onChange={(e) => {
                    setEditableGroupName(e.target.value);
                    onGroupNameChange?.(e.target.value);
                  }}
                  placeholder="Enter group name (e.g., A, B, C)"
                  maxLength={50}
                  className="mb-2"
                  style={{ borderRadius: '8px', border: '2px solid #e0e0e0' }}
                />
              </div>
            )}

            {/* Add New Teams Section */}
            <div className="mb-3">
              <h6 className="fw-semibold text-success mb-2">
                <i className="ri-add-circle-line me-2"></i>
                Add New Teams to {contextTitle}
              </h6>
              <Select
                classNamePrefix="react-select"
                isMulti
                isLoading={teamsLoading}
                loadingMessage={() => "Loading teams..."}
                noOptionsMessage={() => {
                  if (teamsLoading) return "Loading teams...";
                  if (availableTeams.length === 0) {
                    console.log('No teams available - showing message');
                    return "No teams available for this age category. Check console for debugging info.";
                  }
                  console.log('Teams available but filtered out - showing no options message');
                  return "No teams available";
                }}
                options={(() => {
                  // Safety check: ensure availableTeams is an array and filter out any null/undefined values
                  const safeAvailableTeams = (Array.isArray(availableTeams) ? availableTeams : [])
                    .filter(team => team && team._id);
                  
                  // For group standings, we need different logic based on context
                  if (groupName) {
                    // For group management, filter out teams that are already in ANY group
                    let allExistingTeamIds: string[] = [];
                    
                    if (allGroupStandings && allGroupStandings.length > 0) {
                      // Get team IDs from all groups (filter out null teams)
                      allExistingTeamIds = allGroupStandings.flatMap(groupStandings => 
                        (Array.isArray(groupStandings) ? groupStandings : [])
                          .filter(standing => standing && standing.team && standing.team._id)
                          .map(standing => standing.team._id)
                      );
                    } else {
                      // Fallback: only filter out teams from current group (filter out null teams)
                      allExistingTeamIds = (Array.isArray(standings) ? standings : [])
                        .filter(standing => standing && standing.team && standing.team._id)
                        .map(standing => standing.team._id);
                    }
                    
                    const filteredTeams = safeAvailableTeams.filter(team => 
                      team && team._id && !allExistingTeamIds.includes(team._id)
                    );
                    
                    const options = filteredTeams.map(team => ({
                      value: team._id,
                      label: team.name || team.shortName || 'Unknown Team',
                      shortName: team.shortName || '',
                      logo: team.logo || null
                    }));
                    
                    console.log('Group management - filtered teams:', options.length);
                    console.log('Available teams:', safeAvailableTeams.length);
                    console.log('All existing team IDs (from all groups):', allExistingTeamIds);
                    console.log('Filtered teams (not in any group):', filteredTeams.length);
                    console.log('First few options:', options.slice(0, 3));
                    return options;
                  } else {
                    // For single standings, filter out teams that are already in the current standings (filter out null teams)
                    const existingTeamIds = (Array.isArray(standings) ? standings : [])
                      .filter(standing => standing && standing.team && standing.team._id)
                      .map(standing => standing.team._id);
                    const filteredTeams = safeAvailableTeams.filter(team => 
                      team && team._id && !existingTeamIds.includes(team._id)
                    );
                    
                    const options = filteredTeams.map(team => ({
                      value: team._id,
                      label: team.name || team.shortName || 'Unknown Team',
                      shortName: team.shortName || '',
                      logo: team.logo || null
                    }));
                    
                    console.log('Single standings - filtered teams:', options.length);
                    console.log('Available teams:', safeAvailableTeams.length);
                    console.log('Existing team IDs:', existingTeamIds);
                    console.log('Filtered teams:', filteredTeams.length);
                    return options;
                  }
                })()}
                value={selectedTeams}
                onChange={(newValue) => {
                  console.log('Select onChange called with:', newValue);
                  handleTeamSelection(newValue);
                }}
                placeholder={teamsLoading ? "Loading teams..." : "Search and select teams to add..."}
                isSearchable
                isClearable
                menuPosition="absolute"
                formatOptionLabel={(option: any) => (
                  <div className="d-flex align-items-center">
                    {option.logo ? (
                      <img 
                        src={getImageUrl(option.logo)}
                        alt={option.label}
                        className="rounded-circle me-2"
                        style={{ width: '24px', height: '24px', objectFit: 'cover' }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div 
                        className="rounded-circle bg-light border d-flex align-items-center justify-content-center me-2" 
                        style={{ width: '24px', height: '24px' }}
                      >
                        <i className="ri-team-line text-muted" style={{ fontSize: '12px' }}></i>
                      </div>
                    )}
                    <div>
                      <div className="fw-medium">{option.label}</div>
                      <small className="text-muted">{option.shortName}</small>
                    </div>
                  </div>
                )}
                styles={{
                  control: (base) => ({ 
                    ...base, 
                    minHeight: '45px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    boxShadow: 'none'
                  }),
                  menu: (base) => ({ 
                    ...base, 
                    zIndex: 9999
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: '#e7f1ff',
                    border: '1px solid #b3d7ff',
                    borderRadius: '6px'
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: '#0d6efd',
                    fontSize: '14px'
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: '#0d6efd',
                    '&:hover': {
                      backgroundColor: '#0d6efd',
                      color: 'white'
                    }
                  })
                }}
              />
              
              {/* Debug: Show teams count */}
              {availableTeams.length > 0 && (
                <div className="mt-2">
                  <small className="text-info">
                    <i className="ri-information-line me-1"></i>
                    Debug: {availableTeams.length} teams loaded successfully
                  </small>
                </div>
              )}
              {selectedTeams.length > 0 && (
                <div className="mt-2">
                  <small className="text-success">
                    <i className="ri-check-line me-1"></i>
                    {selectedTeams.length} team{selectedTeams.length !== 1 ? 's' : ''} selected
                  </small>
                </div>
              )}
              
              {/* Add Teams Button - Moved here for better UX */}
              {selectedTeams.length > 0 && (
                <div className="mt-3">
                  <Button
                    variant="success"
                    onClick={handleAddTeams}
                    disabled={addingTeams}
                    className="px-4"
                    style={{ borderRadius: '8px', minHeight: '40px' }}
                  >
                    {addingTeams ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {isEditMode ? 'Saving...' : 'Adding...'}
                      </>
                    ) : (
                      <>
                        <i className={isEditMode ? "ri-save-line me-2" : "ri-add-line me-2"}></i>
                        {isEditMode ? 'Update Teams' : `Add ${selectedTeams.length} Team${selectedTeams.length !== 1 ? 's' : ''}`}
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Update Teams Button - Show when in edit mode and no teams selected but teams exist */}
              {isEditMode && selectedTeams.length === 0 && standings.length > 0 && (
                <div className="mt-3">
                  <Button
                    variant="primary"
                    onClick={() => {
                      // Refresh the teams list
                      loadAvailableTeams(true);
                    }}
                    className="px-4"
                    style={{ borderRadius: '8px', minHeight: '40px' }}
                  >
                    <i className="ri-refresh-line me-2"></i>
                    Refresh Teams List
                  </Button>
                </div>
              )}
            </div>

            {/* Currently Selected Teams Section */}
            <div className="mb-3">
              <h6 className="fw-semibold text-primary mb-2">
                <i className="ri-team-line me-2"></i>
                Currently in {contextTitle} ({standings.length} teams)
              </h6>
              {standings.length > 0 ? (
                <div className="row g-2">
                  {standings
                    .filter(standing => standing && standing.team && standing.team._id)
                    .map((standing, index) => (
                    <div key={standing.team._id} className="col-md-6 col-lg-4">
                      <div className="d-flex align-items-center justify-content-between p-2 bg-white rounded border">
                        <div className="d-flex align-items-center">
                          {standing.team.logo ? (
                            <img 
                              src={getImageUrl(standing.team.logo)}
                              alt={standing.team.name || 'Team logo'}
                              className="rounded-circle me-2"
                              style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div 
                              className="rounded-circle bg-light border d-flex align-items-center justify-content-center me-2 team-fallback-icon" 
                              style={{ 
                                width: '32px', 
                                height: '32px',
                                display: 'flex'
                              }}
                            >
                              <i className="ri-team-line text-muted" style={{ fontSize: '16px' }}></i>
                            </div>
                          )}
                          <div>
                            <div className="fw-medium text-dark">{standing.team.name || 'Unknown Team'}</div>
                            <small className="text-muted">{standing.team.shortName || ''}</small>
                          </div>
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveTeam(standing)}
                          className="p-1"
                          style={{ width: '28px', height: '28px' }}
                        >
                          <i className="ri-delete-bin-line" style={{ fontSize: '12px' }}></i>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="ri-trophy-line" style={{ fontSize: '48px', opacity: 0.3, color: '#6c757d' }}></i>
                  <p className="text-muted mt-2 mb-0">No teams in this {groupName ? 'group' : 'standings'} yet</p>
                  <small className="text-muted">Add teams using the dropdown above</small>
                </div>
              )}
            </div>
          </div>

          <div className="d-flex justify-content-end mt-4">
            <Button
              variant="outline-secondary"
              onClick={() => {
                onCloseCard();
                setSelectedTeams([]);
                onSetEditMode(false);
              }}
              className="px-4"
              style={{ borderRadius: '8px', minHeight: '40px' }}
            >
              Cancel
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="ri-delete-bin-line me-2 text-danger"></i>
            Remove Team from {groupName ? `Group ${editableGroupName}` : 'Standings'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <i className="ri-error-warning-line text-warning" style={{ fontSize: '48px' }}></i>
            </div>
            <p className="mb-0">
              Are you sure you want to remove <strong>{teamToDelete?.team?.name || 'this team'}</strong> from {groupName ? `Group ${editableGroupName}` : 'standings'}? 
              This action cannot be undone.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Removing...
              </>
            ) : (
              <>
                <i className="ri-delete-bin-line me-2"></i>
                Yes, Remove
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TeamManagementCard;
